import type { MovementTarget } from './MovementDialogs.js';
import {
  adminRankLocked,
  editActions,
  type MovementAction,
  movementActionHint,
} from './sign-on-off.js';
import type { CrewAssignment, CrewProfileView, MovementTab } from './types.js';

/**
 * Kartlı kişinin Joining / Sign-Off işlemlerinin kuralları: Crew Profile ve Settings ▸ Users
 * çekmecesi aynı işlevleri kullanır (ADR 0131 §7; ADR 0155 Ek, sahip 2026-11-02 gece). Saf
 * işlevlerdir; sunucu aynı kuralları yeniden denetler.
 */

/**
 * Atamanın Joining / Sign-Off işlemleri (ADR 0131 §7): Sign-Off'u olan atamada ayrılış geri
 * alınır; gerçekleşmemiş katılış iptal edilir (`users.void`). Rank change yeni atama açar ve
 * `users.create` ister (ADR 0151 §4). `canMove` kapalıysa (`cardRowMovable`) yalnız History:
 * geçmiş salt okumadır (Sign On / Off `rowActions` ile aynı kural).
 */
export function assignmentActions(
  row: CrewAssignment,
  today: string,
  canVoid: boolean,
  canRankChange = true,
  canMove = true,
): MovementAction[] {
  if (!canMove) return ['history'];
  // Katılmamış kişide Amend öne çıkar (sahip 2026-10-31)
  const actions: MovementAction[] = editActions(row, today).filter(
    (action) => action !== 'rankChange' || canRankChange,
  );
  if (row.validFrom > today && canVoid) actions.push('cancel');
  if (row.validTo !== null) actions.push('revoke');
  actions.push('history');
  return actions;
}

/**
 * Kartta satırın hareket işlemleri (Sign off, Rank change, Amend, Cancel, Revoke) açık mı:
 * kişinin kendi kartı (`canMove`; U39) ve yönetici rütbeli satır (ADR 0150 §1) yönetici olmayana
 * kapalı, History kalır (ADR 0151 §4). Eski sunucu bayrakları göndermezse açık; sunucu yine denetler.
 */
export function cardRowMovable(
  view: Pick<CrewProfileView, 'canMove' | 'actorIsAdmin' | 'adminRoleIds'>,
  row: Pick<CrewAssignment, 'roleId'>,
): boolean {
  return view.canMove !== false && !adminRankLocked(row.roleId, view);
}

/** Crew Profile atamasını hareket penceresinin hedefine çevirir. */
export function movementTarget(
  view: Pick<CrewProfileView, 'fullName'>,
  row: CrewAssignment,
  tab: MovementTab,
): MovementTarget {
  const joining = tab === 'joining';
  return {
    assignmentId: row.id,
    rowVersion: row.rowVersion ?? 1,
    fullName: view.fullName,
    validFrom: row.validFrom,
    validTo: row.validTo,
    date: joining ? row.validFrom : (row.validTo ?? row.validFrom),
    port: (joining ? row.joinPort : row.signOffPort) ?? null,
    portCode: (joining ? row.joinPortCode : row.signOffPortCode) ?? null,
    contractNo: row.contractNo ?? null,
    roleId: row.roleId,
    rank: row.rank,
  };
}

/** Çekmecenin sunduğu işlemler (ADR 0155 Ek): Sign off ve Rank change; Amend ve ötekiler kartta. */
export type DrawerMovement = Extract<MovementAction, 'signOff' | 'rankChange'>;

/** Bitmemiş atama (bugün ya da ileride süren): çekmecede işlem satırıdır. */
const unfinished = (row: Pick<CrewAssignment, 'validTo'>, today: string) =>
  row.validTo === null || row.validTo >= today;

/**
 * Çekmecede atamanın işlemleri (ADR 0155 Ek): Crew Profile'ın satır menüsüyle aynı kural
 * (`assignmentActions`, `cardRowMovable`), yalnız Sign off ve Rank change. `users.edit` yoksa
 * (`canEdit`) ya da atama bitmişse işlem yok. `hint`: kapalı işlemin ipucu (`movementActionHint`).
 */
export function drawerRowActions(
  view: Pick<
    CrewProfileView,
    'today' | 'canEdit' | 'canVoid' | 'canRankChange' | 'canMove' | 'actorIsAdmin' | 'adminRoleIds'
  >,
  row: CrewAssignment,
): { action: DrawerMovement; hint: string | null }[] {
  const today = view.today ?? new Date().toISOString().slice(0, 10);
  if (view.canEdit !== true || !unfinished(row, today)) return [];
  return assignmentActions(
    row,
    today,
    view.canVoid === true,
    view.canRankChange !== false,
    cardRowMovable(view, row),
  )
    .filter((action): action is DrawerMovement => action === 'signOff' || action === 'rankChange')
    .map((action) => ({ action, hint: movementActionHint(action, row, today) }));
}

/**
 * Çekmecede "Sign on" (ADR 0155 Ek): Crew Profile başlığındaki kural; yetki (`canSignOn`:
 * `users.edit` + `users.create`), kart bilgisi var ve kişi bugün gemide değil (başlamış, açık
 * atama yok). Uyruk ve doğum tarihi eksikse sunucu `CREW_PROFILE_INCOMPLETE` verir.
 */
export function drawerCanSignOn(
  view: Pick<CrewProfileView, 'today' | 'canSignOn' | 'profile' | 'assignments'>,
): boolean {
  const today = view.today ?? new Date().toISOString().slice(0, 10);
  const current = view.assignments.some((row) => row.validTo === null && row.validFrom <= today);
  return view.canSignOn === true && view.profile !== null && !current;
}

/** Çekmecenin listesi: bütün atamalar, en yeni katılış önce (bitmişler salt okunur). */
export function drawerAssignments(view: Pick<CrewProfileView, 'assignments'>): CrewAssignment[] {
  return [...view.assignments].sort(
    (a, b) => b.validFrom.localeCompare(a.validFrom) || a.id.localeCompare(b.id),
  );
}
